// Uniform spatial hash over the 200×200 toroidal arena with 4 u cells.
// Rebuilt every tick; zero allocation in hot path.
// The grid wraps at the seam: cell coordinates are taken modulo 50 (the arena
// is an exact multiple of the cell size), and query distance tests use the
// shortest signed delta across the seam. See GDD §14.4.

import type { Vec2 } from '../types.js';

const CELL_SIZE = 4;
const ARENA_SIZE = 200;
const HALF = ARENA_SIZE / 2;
const CELLS_PER_SIDE = Math.ceil(ARENA_SIZE / CELL_SIZE); // 50

export interface Collider {
  id: number;
  pos: Vec2;
  hitbox_radius: number;
  alive: boolean;
}

// Wrap a scalar into [-HALF, HALF); mirrors wrapArena in collision.ts, kept
// local so this module stays dependency-free.
function wrapAxis(v: number): number {
  let r = (v + HALF) % ARENA_SIZE;
  if (r < 0) r += ARENA_SIZE;
  return r - HALF;
}

// Fast shortest signed delta across the seam. Branch-based; valid when
// inputs are already within one arena of each other.
function wrapDeltaAxis(d: number): number {
  if (d > HALF) return d - ARENA_SIZE;
  if (d < -HALF) return d + ARENA_SIZE;
  return d;
}

export class SpatialHash {
  // Flat linked-list storage. Each inserted entity occupies one slot per cell it overlaps.
  private next: Int32Array;
  private entityId: Int32Array;
  private heads: Int32Array;
  private entityCount = 0;
  private readonly maxEntities: number;
  private visitMark: Uint32Array;
  private visitGeneration = 1;

  constructor(maxEntities: number) {
    this.maxEntities = maxEntities;
    // Worst case: one entity can occupy up to 4 cells (radius ~cell size).
    // Reserve 4 slots per entity for multi-cell insertion.
    this.next = new Int32Array(maxEntities * 4).fill(-1);
    this.entityId = new Int32Array(maxEntities * 4).fill(-1);
    this.heads = new Int32Array(CELLS_PER_SIDE * CELLS_PER_SIDE).fill(-1);
    this.visitMark = new Uint32Array(maxEntities + 1);
  }

  // Raw (unwrapped) cell coordinate; may fall outside [0, CELLS_PER_SIDE)
  // near the seam. Wrap with wrapCell before indexing.
  private cellCoord(v: number): number {
    return Math.floor((v + HALF) / CELL_SIZE);
  }

  private wrapCell(c: number): number {
    const r = c % CELLS_PER_SIDE;
    return r < 0 ? r + CELLS_PER_SIDE : r;
  }

  private cellIndex(cx: number, cy: number): number {
    return cy * CELLS_PER_SIDE + cx;
  }

  clear(): void {
    this.heads.fill(-1);
    this.entityCount = 0;
    if (this.visitGeneration > 0xFFFFFFF0) {
      this.visitMark.fill(0);
      this.visitGeneration = 1;
    }
  }

  insert(entity: Collider): void {
    if (!entity.alive) return;
    const r = entity.hitbox_radius;
    // Positions are normally already wrapped by the sim; only normalize when
    // they drift out of range (cheap branch instead of unconditional modulo).
    let px = entity.pos.x;
    let py = entity.pos.y;
    if (px < -HALF || px >= HALF) px = wrapAxis(px);
    if (py < -HALF || py >= HALF) py = wrapAxis(py);
    const minX = this.cellCoord(px - r);
    const maxX = this.cellCoord(px + r);
    const minY = this.cellCoord(py - r);
    const maxY = this.cellCoord(py + r);

    for (let cy = minY; cy <= maxY; cy++) {
      const wy = this.wrapCell(cy);
      for (let cx = minX; cx <= maxX; cx++) {
        const slot = this.entityCount++;
        if (slot >= this.entityId.length) {
          this.entityCount--;
          return;
        }
        const cell = this.cellIndex(this.wrapCell(cx), wy);
        this.entityId[slot] = entity.id;
        this.next[slot] = this.heads[cell];
        this.heads[cell] = slot;
      }
    }
  }

  forEachInCircle(
    center: Vec2,
    radius: number,
    getPos: (id: number) => Vec2 | undefined,
    callback: (id: number) => void,
    excludeId: number = -1
  ): void {
    let cx0 = center.x;
    let cy0 = center.y;
    if (cx0 < -HALF || cx0 >= HALF) cx0 = wrapAxis(cx0);
    if (cy0 < -HALF || cy0 >= HALF) cy0 = wrapAxis(cy0);
    const minX = this.cellCoord(cx0 - radius);
    const maxX = this.cellCoord(cx0 + radius);
    const minY = this.cellCoord(cy0 - radius);
    const maxY = this.cellCoord(cy0 + radius);

    const r2 = radius * radius;
    const gen = this.visitGeneration++;

    for (let cy = minY; cy <= maxY; cy++) {
      const wy = this.wrapCell(cy);
      for (let cx = minX; cx <= maxX; cx++) {
        const cell = this.cellIndex(this.wrapCell(cx), wy);
        let slot = this.heads[cell];
        while (slot >= 0) {
          const eid = this.entityId[slot];
          if (eid !== excludeId && this.visitMark[eid] !== gen) {
            this.visitMark[eid] = gen;
            const pos = getPos(eid);
            if (pos) {
              // Shortest signed delta across the seam.
              const dx = wrapDeltaAxis(pos.x - cx0);
              const dy = wrapDeltaAxis(pos.y - cy0);
              if (dx * dx + dy * dy <= r2) {
                callback(eid);
              }
            }
          }
          slot = this.next[slot];
        }
      }
    }
  }

  /**
   * Rectangular query with the same seam semantics as forEachInCircle: the
   * rect may straddle the ±100 boundary and still find wrapped neighbors.
   */
  forEachInRect(
    center: Vec2,
    halfWidth: number,
    halfHeight: number,
    getPos: (id: number) => Vec2 | undefined,
    callback: (id: number) => void,
    excludeId: number = -1
  ): void {
    const cx0 = wrapAxis(center.x);
    const cy0 = wrapAxis(center.y);
    const minX = this.cellCoord(cx0 - halfWidth);
    const maxX = this.cellCoord(cx0 + halfWidth);
    const minY = this.cellCoord(cy0 - halfHeight);
    const maxY = this.cellCoord(cy0 + halfHeight);

    const gen = this.visitGeneration++;

    for (let cy = minY; cy <= maxY; cy++) {
      const wy = this.wrapCell(cy);
      for (let cx = minX; cx <= maxX; cx++) {
        const cell = this.cellIndex(this.wrapCell(cx), wy);
        let slot = this.heads[cell];
        while (slot >= 0) {
          const eid = this.entityId[slot];
          if (eid !== excludeId && this.visitMark[eid] !== gen) {
            this.visitMark[eid] = gen;
            const pos = getPos(eid);
            if (pos) {
              const dx = wrapDeltaAxis(pos.x - cx0);
              const dy = wrapDeltaAxis(pos.y - cy0);
              if (Math.abs(dx) <= halfWidth && Math.abs(dy) <= halfHeight) {
                callback(eid);
              }
            }
          }
          slot = this.next[slot];
        }
      }
    }
  }

  getCount(): number {
    return this.entityCount;
  }
}
